/** Fallback local del precio mensual por cupo extra. La fuente es `getExtraUserPriceUsd`. */
export const EXTRA_USER_PRICE_USD = 5;

/**
 * Interruptor de la venta de cupos de usuarios asociados (ADM-790). En `false` la app no
 * ofrece comprar cupos, el pago de plan no los cobra y crear usuarios no tiene tope.
 * Espejo de la misma constante en api, api-admin, cron-jobs y signup: reactivar la venta
 * exige cambiarla en los 5 repos y desplegarlos en la misma ventana.
 */
export const EXTRA_USERS_SALES_ENABLED = false;

function resolveExtraUserPriceUsd(unitPriceUsd: unknown): number {
    return Number(unitPriceUsd) || EXTRA_USER_PRICE_USD;
}

/**
 * Precio mensual USD por cupo extra (sin IVA ni descuento) de `GET /api/plans`
 * (`data.extraUserPriceUsd`, la `EXTRA_USER_PRICE_USD` de api). api-admin cobra y
 * valida con su propia `EXTRA_USER_PRICE_USD`: son dos constantes que deben valer lo
 * mismo, o el cobro se rechaza por monto inconsistente.
 * Ausente, 0 o no numérico → `EXTRA_USER_PRICE_USD`.
 */
export function getExtraUserPriceUsd(bundle?: { extraUserPriceUsd?: number } | null): number {
    return resolveExtraUserPriceUsd(bundle?.extraUserPriceUsd);
}

/** Tope de asientos extra. Espejo de `api/src/constants/extraUsers.ts` y api-admin. */
export const MAX_PURCHASED_USERS = 99;

/** Permiso que exige el gate de cupos de `api` (`verifyExtraUsersSeatChange`). */
export const MANAGE_USERS_PERMISSION = 'create-and-edit-users';

/**
 * Espejo en la UI del gate de `api`: cambiar la cantidad de cupos cobrados fija
 * el importe que se cobra y, al acreditarse, bloquea usuarios asociados, así que
 * exige `create-and-edit-users`. Sin el permiso el server responde 403.
 * Con la venta apagada (`salesEnabled`, ADM-790) nadie cambia cupos.
 */
export function canManageExtraUserSeats(user: { permissions?: { COMMERCE?: string[] } | null } | null | undefined, salesEnabled: boolean = EXTRA_USERS_SALES_ENABLED): boolean {
    if (!salesEnabled) {
        return false;
    }
    const commerce = user?.permissions?.COMMERCE;
    return Array.isArray(commerce) && commerce.includes(MANAGE_USERS_PERMISSION);
}

export function extraUsersMonthsForFrequency(paymentFrequency: string | null | undefined): number {
    switch ((paymentFrequency || '').toString().trim().toLowerCase()) {
        case 'trimestral':
            return 3;
        case 'semestral':
            return 6;
        case 'anual':
            return 12;
        default:
            return 1;
    }
}

/** Factor de frecuencia de renovación (mismo que el plan: 10% / 20% / 30%). */
export function extraUsersFrequencyFactor(paymentFrequency: string | null | undefined): number {
    switch ((paymentFrequency || '').toString().trim().toLowerCase()) {
        case 'trimestral':
            return 0.9;
        case 'semestral':
            return 0.8;
        case 'anual':
            return 0.7;
        default:
            return 1;
    }
}

/** `errorCode` del 409 de api-admin: mix con cupos y la fecha de pago ya vencida. */
export const EXTRA_USERS_MIX_RENEWAL_DUE = 'EXTRA_USERS_MIX_RENEWAL_DUE';

/** Copy del 409 `EXTRA_USERS_MIX_RENEWAL_DUE` (api-admin); la UI lo anticipa con el mismo texto. */
export const EXTRA_USERS_MIX_RENEWAL_DUE_MESSAGE = 'Tu fecha de pago ya pasó. Paga tu renovación y suma ahí los usuarios adicionales.';

/**
 * `errorCode` del 409 de api-admin (reenviado tal cual por api) cuando llega una compra de
 * cupos con la venta apagada (ADM-790): `pendingPurchasedUsers` distinto del vigente o una
 * mix con `extraUsersToAdd > 0`. Se muestra como aviso, nunca como rechazo de tarjeta.
 */
export const EXTRA_USERS_SALES_DISABLED = 'EXTRA_USERS_SALES_DISABLED';

/** Copy del 409 `EXTRA_USERS_SALES_DISABLED` (api-admin), solo como respaldo si el body no lo trae. */
export const EXTRA_USERS_SALES_DISABLED_TITLE = 'Venta no disponible';
export const EXTRA_USERS_SALES_DISABLED_MESSAGE = 'La venta de usuarios adicionales no está disponible';

/** El código va en la raíz del body (`err.error.errorCode`), no dentro de `data`. */
export function isExtraUsersSalesDisabledError(err: any): boolean {
    return err?.status === 409 && err?.error?.errorCode === EXTRA_USERS_SALES_DISABLED;
}

const MIX_FREQUENCIES = ['mensual', 'trimestral', 'semestral', 'anual'];
/** Caracas es UTC-4 fijo (sin horario de verano). */
const CARACAS_OFFSET_MS = 4 * 60 * 60 * 1000;

interface CalendarDay {
    y: number;
    m: number; // 0-11
    d: number;
}

const dayKey = ({ y, m, d }: CalendarDay): number => y * 10000 + m * 100 + d;

/**
 * Día de Caracas ('YYYY-MM-DD') de un **instante**. No le pases un día de calendario
 * 'YYYY-MM-DD': `new Date('2026-10-04')` es medianoche UTC y restarle 4 h da el día anterior.
 */
export function caracasDay(instant: Date | number): string | null {
    const time = instant instanceof Date ? instant.getTime() : Number(instant);
    if (!Number.isFinite(time)) {
        return null;
    }
    const caracas = new Date(time - CARACAS_OFFSET_MS);
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${caracas.getUTCFullYear()}-${pad(caracas.getUTCMonth() + 1)}-${pad(caracas.getUTCDate())}`;
}

/** Suma meses con la semántica de `moment.add`: mismo día o, si no existe, el último del mes. */
function addCalendarMonths({ y, m, d }: CalendarDay, months: number): CalendarDay {
    const total = m + months;
    const ty = y + Math.floor(total / 12);
    const tm = ((total % 12) + 12) % 12;
    const lastDay = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
    return { y: ty, m: tm, d: Math.min(d, lastDay) };
}

/**
 * Meses de cupos extra que cobra una compra mix: del día de hoy (Caracas) hasta la
 * próxima fecha de pago, redondeando hacia arriba, mínimo 1 y con tope en los meses
 * de la frecuencia. **0 = la fecha ya venció: la compra con cupos se bloquea** (api-admin
 * responde 409 `EXTRA_USERS_MIX_RENEWAL_DUE`).
 *
 * Espejo exacto de `mixExtraUsersMonths` de api-admin (`src/constants/extraUsers.ts`),
 * con los mismos vectores de paridad: si cambia uno, cambia el otro en el mismo ciclo.
 * `referencia` es `bundle.nextPayment.dateToPay` tal cual (api ya resuelve `procesando`).
 * El día de vencimiento es el día UTC de la referencia (se guarda `D T00:00Z` o `D T04:00Z`).
 */
export function mixExtraUsersMonths(referencia: Date | string | null | undefined, paymentFrequency: string | null | undefined, now: Date = new Date()): number {
    const freq = (paymentFrequency || '').toString().trim().toLowerCase() || 'mensual';
    if (!MIX_FREQUENCIES.includes(freq)) {
        return 1;
    }
    if (referencia == null || referencia === '') {
        return 1;
    }
    const ref = new Date(referencia);
    if (Number.isNaN(ref.getTime())) {
        return 1;
    }
    const caracas = new Date(now.getTime() - CARACAS_OFFSET_MS);
    const today: CalendarDay = { y: caracas.getUTCFullYear(), m: caracas.getUTCMonth(), d: caracas.getUTCDate() };
    const due: CalendarDay = { y: ref.getUTCFullYear(), m: ref.getUTCMonth(), d: ref.getUTCDate() };
    if (dayKey(due) <= dayKey(today)) {
        return 0;
    }
    const cap = extraUsersMonthsForFrequency(freq);
    let months = 1;
    while (months < cap && dayKey(addCalendarMonths(today, months)) < dayKey(due)) {
        months++;
    }
    return months;
}

export function normalizePurchasedUsers(value: unknown): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return 0;
    }
    return Math.min(MAX_PURCHASED_USERS, Math.max(0, Math.floor(numeric)));
}

/** Espejo de `PLAN_INCLUDED_USERS_BY_TYPE` / `DEFAULT_PLAN_INCLUDED_USERS` de api y api-admin. */
const PLAN_INCLUDED_USERS_BY_TYPE: Record<string, number> = { gratis: 1, economico: 1, estandar: 3, fiscal: 3 };
const DEFAULT_PLAN_INCLUDED_USERS = 1;

/**
 * Usuarios que incluye un plan, espejo de `getPlanLimitUsers` (api / api-admin):
 * `plan.users` si es > 0; si falta, el mapa por tipo; si el tipo no está, 1.
 * api-admin mide la ocupación al acreditar con este valor del plan del tenant.
 */
export function planIncludedUsers(plan: { type?: string | null; users?: number | null } | null | undefined): number {
    const stored = Number(plan?.users);
    if (Number.isFinite(stored) && stored > 0) {
        return Math.floor(stored);
    }
    const type = (plan?.type ?? '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
    return PLAN_INCLUDED_USERS_BY_TYPE[type] ?? DEFAULT_PLAN_INCLUDED_USERS;
}

/**
 * Mínimo de cupos adicionales que se pueden dejar al pagar: un cupo que ocupa un
 * usuario no se quita (primero se elimina el usuario). Tope = los vigentes: si la
 * ocupación ya desborda el total (heredados), no se bajan, pero tampoco se suben
 * solos, para no cobrar cupos que el comercio no pidió.
 *
 * `occupiedSeats` es `seats.createdUsers` (usuarios que ocupan cupo según `api`).
 * La regla es solo de UI: si llega una reducción por debajo, api-admin bloquea al
 * pagar a los asociados más recientes que sobren.
 */
export function minPurchasedUsersForOccupancy({ currentPurchased, includedSeats, occupiedSeats }: { currentPurchased: unknown; includedSeats: unknown; occupiedSeats: unknown }): number {
    const occupied = Math.max(0, Math.floor(Number(occupiedSeats) || 0));
    const included = Math.max(0, Math.floor(Number(includedSeats) || 0));
    return Math.min(normalizePurchasedUsers(currentPurchased), Math.max(0, occupied - included));
}

/**
 * Base de extras: asientos × precio unitario × meses × factor. Nunca 3x2 ni cupón.
 * `unitPriceUsd` viene de `getExtraUserPriceUsd(bundle)`; sin él, $5.
 */
export function extraUsersBaseUSD(purchasedUsers: unknown, paymentFrequency: string | null | undefined, unitPriceUsd?: number): number {
    const seats = normalizePurchasedUsers(purchasedUsers);
    const unit = resolveExtraUserPriceUsd(unitPriceUsd);
    return Number((seats * unit * extraUsersMonthsForFrequency(paymentFrequency) * extraUsersFrequencyFactor(paymentFrequency)).toFixed(2));
}

export function extraUsersMonthlyUsd(purchasedUsers: unknown, unitPriceUsd?: number): number {
    return Number((normalizePurchasedUsers(purchasedUsers) * resolveExtraUserPriceUsd(unitPriceUsd)).toFixed(2));
}

const EXTRA_USERS_IVA_RATE = 0.16;

/** Δ de base USD entre cupos actuales y los que se van a cobrar. */
export function extraUsersDeltaBaseUSD(currentSeats: unknown, nextSeats: unknown, paymentFrequency: string | null | undefined, unitPriceUsd?: number): number {
    return Number((extraUsersBaseUSD(nextSeats, paymentFrequency, unitPriceUsd) - extraUsersBaseUSD(currentSeats, paymentFrequency, unitPriceUsd)).toFixed(2));
}

/**
 * Δ payable (base + IVA si plus) alineado con `computeExtraUsersAmountDelta`
 * de api-admin: `bill.amount + deltaAmount`.
 */
export function extraUsersDeltaAmountUSD(currentSeats: unknown, nextSeats: unknown, paymentFrequency: string | null | undefined, plus: boolean, unitPriceUsd?: number): number {
    const deltaBase = extraUsersDeltaBaseUSD(currentSeats, nextSeats, paymentFrequency, unitPriceUsd);
    const deltaPlus = plus ? Number((deltaBase * EXTRA_USERS_IVA_RATE).toFixed(2)) : 0;
    return Number((deltaBase + deltaPlus).toFixed(2));
}
